import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { listJenisKelamin, listStatusKeluarga } from "@/constants";
import api from "@/lib/axios";
import { formatRupiah } from "@/lib/utils";
import {
  getListHubunganKeluarga,
  getListJenjangPendidikan,
  getListPangkatGolongan,
  getListPekerjaan,
  getListWilayah,
} from "@/services/getData";
import {
  DataKeluarga,
  ErrorType,
  HubunganKeluarga,
  JenjangPendidikan,
  Option,
  PangkatGolongan,
  Pekerjaan,
  Status,
  Wilayah,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  Briefcase,
  FileText,
  Info,
  Loader2,
  MapPin,
  UploadCloud,
  UserCircle,
} from "lucide-react"; // Tambahkan icon dari lucide
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa6";
import { MdModeEdit } from "react-icons/md";
import { RiDeleteBack2Line } from "react-icons/ri";

// ... (Fungsi postData, patchData, deleteData tetap sama persis)
const postData = async (newData: FormData) => {
  const response = await api.post(
    "/siswa/daftar-kembali/data-keluarga/store",
    newData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
const patchData = async (newData: FormData) => {
  const response = await api.post(
    "/siswa/daftar-kembali/data-keluarga/update",
    newData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

const deleteData = async (deletedData: FormData) => {
  const id_data_keluarga = deletedData.get("id_data_keluarga");

  const response = await api({
    url: "/siswa/daftar-kembali/data-keluarga/delete",
    method: "DELETE",
    data: { id_data_keluarga },
  });
  return response.data;
};

export default function Form({
  method,
  closeModal,
  selectedRow,
  idDataPendaftaran,
}: {
  method: "POST" | "PATCH" | "DELETE";
  closeModal: () => void;
  selectedRow?: DataKeluarga | null;
  idDataPendaftaran?: string;
}) {
  // ... (State, Queries, dan Handler logic tetap sama persis)
  const [error, setError] = useState<ErrorType | null>(null);
  const [form, setForm] = useState<DataKeluarga>({
    id_data_keluarga: selectedRow?.id_data_keluarga || "",
    id_data_pendaftaran:
      idDataPendaftaran || selectedRow?.id_data_pendaftaran || "",
    id_ref_golongan: selectedRow?.id_ref_golongan || "",
    id_ref_hubungan_keluarga: selectedRow?.id_ref_hubungan_keluarga
      ? String(selectedRow?.id_ref_hubungan_keluarga)
      : "",
    id_ref_jenjang_pendidikan: selectedRow?.id_ref_jenjang_pendidikan || "",
    id_ref_pekerjaan: selectedRow?.id_ref_pekerjaan || "",
    id_ref_penghasilan: selectedRow?.id_ref_penghasilan || "",
    id_ref_wilayah: selectedRow?.id_ref_wilayah || "",
    provinsi: selectedRow?.provinsi || "",
    kecamatan: selectedRow?.kecamatan || "",
    kabupaten: selectedRow?.kabupaten || "",
    nik: selectedRow?.nik || "",
    nama: selectedRow?.nama || "",
    handphone: selectedRow?.handphone || "",
    telepon: selectedRow?.telepon || "",
    jalan: selectedRow?.jalan || "",
    jk: selectedRow?.jk || "",
    path_slip_gaji: selectedRow?.path_slip_gaji || "",
    penghasilan: selectedRow?.penghasilan_old || "",
    status: selectedRow?.status || "",
    tanggal_lahir: selectedRow?.tanggal_lahir || "",
    tempat_lahir: selectedRow?.tempat_lahir || "",
  });

  const [file, setFile] = useState<File | null>(null);

  const [idFeederProvinsi, setIdFeederProvinsi] = useState("");
  const [idFeederKabupaten, setIdFeederKabupaten] = useState("");

  const [openHubunganKeluarga, setOpenHubunganKeluarga] = useState(false);
  const [openJenisKelamin, setOpenJenisKelamin] = useState(false);
  const [openProvinsi, setOpenProvinsi] = useState(false);
  const [openKabupaten, setOpenKabupaten] = useState(false);
  const [openKecamatan, setOpenKecamatan] = useState(false);
  const [openPendidikan, setOpenPendidikan] = useState(false);
  const [openPekerjaan, setOpenPekerjaan] = useState(false);
  const [openPangkatGolongan, setOpenPangkatGolongan] = useState(false);

  const [searchQueryProvinsi, setSearchQueryProvinsi] = useState("");
  const [searchQueryKabupaten, setSearchQueryKabupaten] = useState("");
  const [searchQueryKecamatan, setSearchQueryKecamatan] = useState("");

  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataKeluarga"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      if (err.response?.data) {
        setError(err.response.data as ErrorType);
      } else {
        setError({
          message: "An unknown error occurred.",
          errors: {},
        });
      }
      toast.error(err.response?.data?.message || "An unknown error occurred.");
    },
  });

  const { data: dataProvinsi, isPending: isPendingProvinsi } = useQuery({
    queryKey: ["listProvinsi", form.provinsi, searchQueryProvinsi],
    queryFn: () =>
      getListWilayah(
        10,
        "1",
        undefined,
        searchQueryProvinsi ? undefined : form.provinsi,
        searchQueryProvinsi,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah?.id_feeder}`,
      })),
  });

  const {
    data: dataKabupaten,
    refetch: refetchKabupaten,
    isPending: isPendingKabupaten,
  } = useQuery({
    queryKey: [
      "listKabupaten",
      idFeederProvinsi,
      searchQueryKabupaten ? undefined : form.kabupaten,
      searchQueryKabupaten,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederProvinsi,
        form.kabupaten,
        searchQueryKabupaten,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah.id_feeder}`,
      })),
    enabled: !!idFeederProvinsi,
  });

  const {
    data: dataKecamatan,
    refetch: refetchKecamatan,
    isPending: isPendingKecamatan,
  } = useQuery({
    queryKey: [
      "listKecamatan",
      idFeederKabupaten,
      searchQueryKecamatan ? undefined : form.kecamatan,
      searchQueryKecamatan,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederKabupaten,
        form.kecamatan,
        searchQueryKecamatan,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
      })),
    enabled: !!idFeederKabupaten,
  });

  const { data: dataHubunganKeluarga } = useQuery({
    queryKey: ["listHubunganKeluarga"],
    queryFn: () => getListHubunganKeluarga(1),
    select: (data) =>
      data?.map((hubunganKeluarga: HubunganKeluarga) => ({
        label: hubunganKeluarga.hubungan_keluarga,
        value: `${hubunganKeluarga.id_ref_hubungan_keluarga}`,
      })),
  });

  const { data: dataJenjangPendidikan } = useQuery({
    queryKey: ["listJenjangPendidikan"],
    queryFn: getListJenjangPendidikan,
    select: (data) =>
      data?.map((jenjangPendidikan: JenjangPendidikan) => ({
        label: jenjangPendidikan.nama_jenjang_pendidikan,
        value: `${jenjangPendidikan.id_ref_jenjang_pendidikan}`,
      })),
  });

  const { data: dataPekerjaan } = useQuery({
    queryKey: ["listPekerjaan"],
    queryFn: getListPekerjaan,
    select: (data) =>
      data
        ?.slice()
        ?.sort((a: Pekerjaan, b: Pekerjaan) => {
          const nameA = a.pekerjaan.toLowerCase();
          const nameB = b.pekerjaan.toLowerCase();

          const isALainnya = nameA.includes("lainnya");
          const isBLainnya = nameB.includes("lainnya");

          if (isALainnya && !isBLainnya) return 1;
          if (!isALainnya && isBLainnya) return -1;

          return a.pekerjaan.localeCompare(b.pekerjaan);
        })
        ?.map((pekerjaan: Pekerjaan) => ({
          label: pekerjaan.pekerjaan,
          value: `${pekerjaan.id_ref_pekerjaan}`,
        })),
  });

  const selectedPekerjaan =
    dataPekerjaan &&
    dataPekerjaan?.find((item: Option) => {
      return item.value == form.id_ref_pekerjaan;
    });

  const { data: dataPengkatGolongan } = useQuery({
    queryKey: ["listPangkatGolongan"],
    queryFn: getListPangkatGolongan,
    select: (data) =>
      data?.map((pangkatGolongan: PangkatGolongan) => ({
        label: `${pangkatGolongan.pangkat}-${pangkatGolongan.golongan}`,
        value: `${pangkatGolongan.id_ref_golongan}`,
      })),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB dalam bytes

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("Ukuran file terlalu besar. Maksimal adalah 1MB.");
        e.target.value = "";
        return;
      }
      setFile(file);
      toast.success("File berhasil dipilih.");
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const formData = new FormData();

    if (isPatch || isDelete) {
      formData.append(
        "id_data_keluarga",
        form.id_data_keluarga?.toString() || "",
      );
    }

    formData.append(
      "id_data_pendaftaran",
      form.id_data_pendaftaran?.toString() || "",
    );
    formData.append("id_ref_golongan", form.id_ref_golongan?.toString() || "");
    formData.append(
      "id_ref_hubungan_keluarga",
      form.id_ref_hubungan_keluarga?.toString() || "",
    );
    formData.append(
      "id_ref_jenjang_pendidikan",
      form.id_ref_jenjang_pendidikan?.toString() || "",
    );
    formData.append(
      "id_ref_pekerjaan",
      form.id_ref_pekerjaan?.toString() || "",
    );
    formData.append(
      "id_ref_penghasilan",
      form.id_ref_penghasilan?.toString() || "",
    );
    formData.append("provinsi", form.provinsi || "");
    formData.append("kecamatan", form.kecamatan || "");
    formData.append("kabupaten", form.kabupaten || "");
    formData.append("nik", form.nik || "");
    formData.append("nama", form.nama || "");
    formData.append("handphone", form.handphone || "");
    formData.append("jalan", form.jalan || "");
    formData.append("jk", form.jk || "");
    formData.append("penghasilan", form.penghasilan?.toString() || "0");
    formData.append("status", form.status || "");
    formData.append("tanggal_lahir", form.tanggal_lahir || "");
    formData.append("tempat_lahir", form.tempat_lahir || "");

    if (file) formData.append("file_slip_gaji", file);

    mutate(formData);
  };

  useEffect(() => {
    if (form.provinsi) refetchKabupaten();
  }, [form.provinsi, refetchKabupaten]);

  useEffect(() => {
    if (form.kabupaten) refetchKecamatan();
  }, [form.kabupaten, refetchKecamatan]);

  useEffect(() => {
    const selectedProvinsi = dataProvinsi?.find(
      (provinsi: Option) => provinsi?.value == form.provinsi,
    );
    if (selectedProvinsi) setIdFeederProvinsi(selectedProvinsi?.id_feeder);

    const selectedKabupaten = dataKabupaten?.find(
      (kabupaten: Option) => kabupaten?.value == form.kabupaten,
    );
    if (selectedKabupaten) setIdFeederKabupaten(selectedKabupaten?.id_feeder);
  }, [
    setIdFeederProvinsi,
    setIdFeederKabupaten,
    dataProvinsi,
    dataKabupaten,
    form,
  ]);

  const selectedHubunganKeluarga = dataHubunganKeluarga?.find(
    (option: Option) => option.value == form.id_ref_hubungan_keluarga,
  );
  const isAyah = selectedHubunganKeluarga?.label?.startsWith("Ayah");
  const isIbu = selectedHubunganKeluarga?.label?.startsWith("Ibu");

  useEffect(() => {
    if (isAyah) setForm((prev) => ({ ...prev, jk: "L" }));
    else if (isIbu) setForm((prev) => ({ ...prev, jk: "P" }));
    else setForm((prev) => ({ ...prev, jk: selectedRow?.jk }));
  }, [isAyah, isIbu]);

  // View Delete
  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Hapus Data Keluarga"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.nama}"?`}
        />
        <div className="flex gap-3 justify-end mt-4">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-2"
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Ya, Hapus Data"
            )}
          </Button>
        </div>
      </form>
    );

  // View Insert / Edit
  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Tambah Data Keluarga" : "Ubah Data Keluarga"}
      />

      <div className="flex flex-col gap-8">
        {/* ========================================================= */}
        {/* SECTION 1: IDENTITAS & STATUS                             */}
        {/* ========================================================= */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <UserCircle className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">
              Identitas Diri & Status
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label required>Hubungan Keluarga</Label>
              <Combobox
                open={openHubunganKeluarga}
                setOpen={setOpenHubunganKeluarga}
                value={form.id_ref_hubungan_keluarga || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    id_ref_hubungan_keluarga: newValue,
                  }))
                }
                options={dataHubunganKeluarga}
                placeholder="Pilih hubungan keluarga"
              />
              <InputError
                text={error?.errors?.id_ref_hubungan_keluarga?.join(",")}
              />
            </div>

            {/* Status (Dibuat lebih modern menggunakan Flex Card/Segmented) */}
            <div className="md:col-span-2">
              <Label required className="mb-2 block">
                Status Anggota Keluarga
              </Label>
              <RadioGroup
                required
                value={form.status}
                onValueChange={(value: Status) =>
                  setForm((prev) => ({ ...prev, status: value }))
                }
                className="flex flex-wrap gap-4"
              >
                {listStatusKeluarga.map((option: Option, index: number) => (
                  <Label
                    key={index}
                    htmlFor={`status-${index}`}
                    className={`flex items-center space-x-2 border rounded-lg px-4 py-3 cursor-pointer transition-colors ${
                      form.status === option.value
                        ? "bg-primary/5 border-primary text-primary dark:bg-primary/20"
                        : "bg-transparent border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900"
                    }`}
                  >
                    <RadioGroupItem
                      id={`status-${index}`}
                      value={option.value}
                      required={form.status === ""}
                    />
                    <span>{option.label}</span>
                  </Label>
                ))}
              </RadioGroup>
              <InputError text={error?.errors?.status?.join(",")} />
            </div>

            <div>
              <Label required>Nama Lengkap</Label>
              <Input
                required
                placeholder="Ketik nama lengkap..."
                value={form.nama}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, nama: event.target.value }))
                }
              />
              <InputError text={error?.errors?.nama?.join(",")} />
            </div>

            <div>
              <Label required>NIK (Nomor Induk Kependudukan)</Label>
              <Input
                required
                maxLength={16}
                minLength={16}
                placeholder="Ketik 16 digit NIK..."
                value={form.nik}
                pattern="\d*"
                inputMode="numeric"
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /\D/g,
                    "",
                  );
                }}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    nik: e.target.value.replace(/\D/g, ""),
                  }))
                }
              />
              <InputError text={error?.errors?.nik?.join(",")} />
            </div>

            <div>
              <Label required>Jenis Kelamin</Label>
              <Combobox
                open={openJenisKelamin}
                setOpen={setOpenJenisKelamin}
                value={form.jk || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({ ...prev, jk: newValue as "L" | "P" }))
                }
                options={listJenisKelamin}
                placeholder="Pilih jenis kelamin"
              />
              <InputError text={error?.errors?.jk?.join(",")} />
            </div>

            <div className="grid grid-cols-2 gap-3 md:col-span-1">
              <div>
                <Label required>Tempat Lahir</Label>
                <Input
                  required
                  placeholder="Tempat lahir..."
                  value={form.tempat_lahir}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      tempat_lahir: e.target.value,
                    }))
                  }
                />
                <InputError text={error?.errors?.tempat_lahir?.join(",")} />
              </div>
              <div>
                <Label required>Tanggal Lahir</Label>
                <Input
                  required
                  type="date"
                  value={form.tanggal_lahir}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      tanggal_lahir: e.target.value,
                    }))
                  }
                />
                <InputError text={error?.errors?.tanggal_lahir?.join(",")} />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================================= */}
        {/* SECTION 2: ALAMAT & KONTAK                                */}
        {/* ========================================================= */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <MapPin className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">
              Alamat & Kontak
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label required>Nomor HP / WhatsApp</Label>
              <Input
                required
                placeholder="Ketik nomor hp aktif..."
                value={form.handphone}
                pattern="\d*"
                inputMode="numeric"
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /\D/g,
                    "",
                  );
                }}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    handphone: e.target.value.replace(/\D/g, ""),
                  }))
                }
              />
              <span className="text-[11px] text-muted-foreground mt-1 block">
                Pastikan nomor aktif dan terhubung WhatsApp.
              </span>
              <InputError text={error?.errors?.handphone?.join(",")} />
            </div>

            <div>
              <Label required>Provinsi</Label>
              <Combobox
                serverSide
                open={openProvinsi}
                setOpen={setOpenProvinsi}
                value={form.provinsi || ""}
                setValue={(newValue) => {
                  setForm((prev) => ({
                    ...prev,
                    provinsi: newValue,
                    kabupaten: "",
                    kecamatan: "",
                  }));
                  const selectedProvinsi = dataProvinsi?.find(
                    (p: Option) => p?.value == newValue,
                  );
                  setIdFeederProvinsi(selectedProvinsi?.id_feeder);
                }}
                options={dataProvinsi}
                placeholder="Pilih provinsi"
                onChange={(e) => setSearchQueryProvinsi(e.target.value)}
                searchQueryValue={searchQueryProvinsi || ""}
                isPending={isPendingProvinsi}
              />
              <InputError text={error?.errors?.provinsi?.join(",")} />
            </div>

            {form.provinsi && (
              <div>
                <Label required>Kabupaten / Kota</Label>
                <Combobox
                  serverSide
                  open={openKabupaten}
                  setOpen={setOpenKabupaten}
                  value={form.kabupaten || ""}
                  setValue={(newValue) => {
                    setForm((prev) => ({
                      ...prev,
                      kabupaten: newValue,
                      kecamatan: "",
                    }));
                    const selectedKabupaten = dataKabupaten?.find(
                      (k: Option) => k?.value == newValue,
                    );
                    setIdFeederKabupaten(selectedKabupaten?.id_feeder);
                  }}
                  options={dataKabupaten}
                  placeholder="Pilih kabupaten"
                  onChange={(e) => setSearchQueryKabupaten(e.target.value)}
                  searchQueryValue={searchQueryKabupaten || ""}
                  isPending={isPendingKabupaten}
                />
                <InputError text={error?.errors?.kabupaten?.join(",")} />
              </div>
            )}

            {form.kabupaten && (
              <div>
                <Label required>Kecamatan</Label>
                <Combobox
                  serverSide
                  open={openKecamatan}
                  setOpen={setOpenKecamatan}
                  value={form.kecamatan || ""}
                  setValue={(newValue) =>
                    setForm((prev) => ({ ...prev, kecamatan: newValue }))
                  }
                  options={dataKecamatan}
                  placeholder="Pilih kecamatan"
                  onChange={(e) => setSearchQueryKecamatan(e.target.value)}
                  searchQueryValue={searchQueryKecamatan || ""}
                  isPending={isPendingKecamatan}
                />
                <InputError text={error?.errors?.kecamatan?.join(",")} />
              </div>
            )}

            <div>
              <Label required>Jalan / Alamat Lengkap</Label>
              <Input
                required
                placeholder="Ketik nama jalan/dusun/banjar..."
                value={form.jalan}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, jalan: e.target.value }))
                }
              />
              <InputError text={error?.errors?.jalan?.join(",")} />
            </div>
          </div>
        </section>

        {/* ========================================================= */}
        {/* SECTION 3: PEKERJAAN & PENGHASILAN                        */}
        {/* ========================================================= */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <Briefcase className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">
              Pekerjaan & Ekonomi
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label required>Jenjang Pendidikan</Label>
              <Combobox
                open={openPendidikan}
                setOpen={setOpenPendidikan}
                value={form.id_ref_jenjang_pendidikan || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    id_ref_jenjang_pendidikan: newValue,
                  }))
                }
                options={dataJenjangPendidikan}
                placeholder="Pilih jenjang pendidikan"
              />
              <InputError
                text={error?.errors?.id_ref_jenjang_pendidikan?.join(",")}
              />
            </div>

            <div>
              <Label required>Pekerjaan</Label>
              <Combobox
                open={openPekerjaan}
                setOpen={setOpenPekerjaan}
                value={form.id_ref_pekerjaan || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    id_ref_pekerjaan: newValue,
                    id_ref_golongan: "",
                  }))
                }
                options={dataPekerjaan}
                placeholder="Pilih pekerjaan"
              />
              <InputError text={error?.errors?.id_ref_pekerjaan?.join(",")} />
            </div>

            {selectedPekerjaan &&
              selectedPekerjaan?.label.startsWith("PNS") && (
                <div>
                  <Label required>Pangkat/Golongan</Label>
                  <Combobox
                    open={openPangkatGolongan}
                    setOpen={setOpenPangkatGolongan}
                    value={form.id_ref_golongan || ""}
                    setValue={(newValue) =>
                      setForm((prev) => ({
                        ...prev,
                        id_ref_golongan: newValue,
                      }))
                    }
                    options={dataPengkatGolongan}
                    placeholder="Pilih pangkat/golongan"
                  />
                  <InputError
                    text={error?.errors?.id_ref_golongan?.join(",")}
                  />
                </div>
              )}

            <div>
              <Label required>Penghasilan Kotor Perbulan</Label>
              <Input
                placeholder="Ketik penghasilan kotor (contoh: 3000000)"
                value={formatRupiah(Number(form.penghasilan))}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/\D/g, "");
                  setForm((prev) => ({
                    ...prev,
                    penghasilan: Number(rawValue),
                  }));
                }}
              />
              <InputError text={error?.errors?.penghasilan?.join(",")} />
            </div>
          </div>
        </section>

        {/* ========================================================= */}
        {/* SECTION 4: BERKAS DOKUMEN                                 */}
        {/* ========================================================= */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <FileText className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">
              Dokumen Bukti
            </h3>
          </div>

          <div className="space-y-4">
            {/* Info Callout untuk instruksi */}
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 p-4 rounded-xl flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200/90 leading-relaxed space-y-1">
                <p className="font-semibold mb-1">
                  Catatan bagi yang tidak memiliki Slip Gaji resmi:
                </p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>
                    Unggah <b>Surat Keterangan Penghasilan</b> dari Lurah/Kepala
                    Desa yang berisi <b>keterangan pekerjaan dan penghasilan</b>
                    <a
                      href="https://static1.undiksha.ac.id/penerimaan/Format/daftarkembali/contoh_keterangan_penghasilan_orangtua.pdf"
                      target="_blank"
                      rel="noreferrer"
                      className="ml-1 text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      (Lihat Contoh)
                    </a>
                    .
                  </li>
                  <li>
                    Jika tidak memiliki penghasilan, tetap unggah Surat
                    Keterangan Tidak Memiliki Penghasilan dari Desa.
                  </li>
                  <li>
                    Jika anggota keluarga sudah meninggal, unggah{" "}
                    <b>Surat Keterangan Kematian</b> dari Rumah Sakit atau Desa.
                  </li>
                </ul>
              </div>
            </div>

            {/* Custom Drag & Drop area look */}
            <div className="border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20 rounded-2xl p-6 relative">
              <Label
                required={form.path_slip_gaji == ""}
                className="flex items-center gap-2 mb-2 font-semibold"
              >
                Unggah Bukti Slip Gaji / Surat Keterangan
              </Label>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex-1">
                  <Input
                    required={form.path_slip_gaji == ""}
                    type="file"
                    accept=".pdf, .jpg, .jpeg, .png"
                    onChange={handleFileChange}
                    className="cursor-pointer file:cursor-pointer file:text-primary file:font-semibold bg-white dark:bg-slate-950"
                  />
                  <div className="flex flex-wrap gap-2 mt-3 text-xs text-muted-foreground">
                    <span>
                      Maksimal:{" "}
                      <Badge variant="secondary" className="text-[10px]">
                        1 MB
                      </Badge>
                    </span>
                    <span>
                      Format:{" "}
                      <Badge variant="secondary" className="text-[10px]">
                        .pdf, .jpg, .png
                      </Badge>
                    </span>
                  </div>
                </div>

                {/* Tampilkan file lama jika ada */}
                {form.path_slip_gaji && (
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0 text-sm h-10 border-primary/20 hover:bg-primary/5 text-primary"
                    onClick={() => {
                      if (form.path_slip_gaji)
                        window.open(
                          form.path_slip_gaji,
                          "_blank",
                          "noopener,noreferrer",
                        );
                    }}
                  >
                    <UploadCloud className="w-4 h-4 mr-2" />
                    Lihat Berkas Saat Ini
                  </Button>
                )}
              </div>
              <InputError text={error?.errors?.file_slip_gaji?.join(",")} />
            </div>
          </div>
        </section>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex justify-end gap-3 pt-5 border-t border-slate-200 dark:border-slate-800">
        <Button
          type="button"
          variant="outline"
          onClick={closeModal}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button type="submit" disabled={isPending} className="min-w-[160px]">
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Menyimpan...
            </>
          ) : isPost ? (
            "Simpan Data Keluarga"
          ) : (
            "Simpan Perubahan"
          )}
        </Button>
      </div>
    </form>
  );
}
